import { AppBar, Badge, Button, Container, IconButton, Toolbar, useMediaQuery, useTheme } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import StorefrontOutlinedIcon from '@material-ui/icons/StorefrontOutlined';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import MobileDrawer from 'src/components/page/MobileDrawer';
import { NavMenu } from 'src/components/page/NavMenu';
import TopBar from 'src/components/page/TopBar';
import {
    ROUTE_ACCOUNT,
    ROUTE_ACCOUNT_DATA,
    ROUTE_ACCOUNT_DOWNLOAD,
    ROUTE_ACCOUNT_ORDERS,
    ROUTE_ACCOUNT_SERVICE,
} from 'src/config/account-routes';
import { ROUTE_ADMIN, ROUTE_CART, ROUTE_HOME, ROUTE_STORE } from 'src/config/routes';
import { useAccount } from 'src/core/hooks/account';
import { useAccountActionCount } from 'src/core/hooks/account-routes';
import { useStoreRoutes } from 'src/core/hooks/store-routes';
import logoImg from 'src/resources/coffeehouse/image.png';
import { getShoppingCart } from 'src/store/cart/selectors';
import { getMyOffers } from 'src/store/offer/selectors';

const AppbarContainer = styled('div')({
    position: 'relative',
});

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    alignItems: 'stretch',
    padding: 0,
    margin: theme.spacing(1.2, 0),
    [theme.breakpoints.down('md')]: {
        margin: theme.spacing(0.9, 0),
    },
}));

const LogoWrapper = styled(Button)({
    padding: 0,
    '& img': {
        height: 'auto',
        width: '200px',
        [(() => '@media (max-width:1280px)')()]: {
            width: '150px',
        },
    },
});

const Grow = styled('div')({
    flexGrow: 1,
});

const NavButtonWrapper = styled('div')(({ theme }) => ({
    '& [class*="MuiButton-root"]': {
        fontSize: '1.3rem',
        lineHeight: '1rem',
        fontWeight: theme.typography.fontWeightBold,
        color: 'inherit',
        textDecoration: 'none',
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
    },
}));

const COFFEEHOUSE_ACCOUNT_ROUTES = [
    ROUTE_ACCOUNT_DATA,
    ROUTE_ACCOUNT_ORDERS,
    ROUTE_ACCOUNT_SERVICE,
    ROUTE_ACCOUNT_DOWNLOAD,
];

const CoffeehouseHeader: React.FC = () => {
    const { isLoggedIn, hasPrivilege } = useAccount();
    const { t } = useTranslation('coffeehouse');
    const accountActionCount = useAccountActionCount();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMedium = useMediaQuery(theme.breakpoints.down('lg'));

    const { routes: storeRoutes } = useStoreRoutes();
    const shopRoutes = useMemo(() => [ROUTE_STORE, ...storeRoutes], [storeRoutes]);
    const accountRoutes = useMemo(() => COFFEEHOUSE_ACCOUNT_ROUTES, []);

    const shoppingCart = useSelector(getShoppingCart);
    const myOffers = useSelector(getMyOffers);
    const cartCount = shoppingCart.content?.itemCount ?? 0;
    const offerCount = myOffers.items.length;

    const actionBadge = (
        <Badge color="primary" badgeContent={accountActionCount || undefined}>
            <PersonOutlineOutlinedIcon />
        </Badge>
    );

    return (
        <header>
            <TopBar loginLabel={t('nav.login')} logoutLabel={t('nav.logout')} />
            <AppbarContainer>
                <AppBar component="div" position="sticky" color="transparent" variant="outlined">
                    <Container>
                        <StyledToolbar>
                            <LogoWrapper disableRipple {...(ROUTE_HOME.componentProps as any)}>
                                <img src={logoImg} alt="Coffeehouse" />
                            </LogoWrapper>
                            <Grow />
                            {isLoggedIn &&
                                (isMobile ? (
                                    <MobileDrawer routes={[shopRoutes]} icon={<MenuIcon />} />
                                ) : (
                                    <NavButtonWrapper>
                                        <NavMenu title={t('nav.shop')} items={storeRoutes} />
                                    </NavButtonWrapper>
                                ))}
                            {isLoggedIn && hasPrivilege('ADMIN') && (
                                <NavButtonWrapper>
                                    <Button {...(ROUTE_ADMIN.componentProps as any)}>{t('nav.adminpanel')}</Button>
                                </NavButtonWrapper>
                            )}
                            {isLoggedIn && (
                                <IconButton
                                    {...(ROUTE_CART.componentProps as any)}
                                    color="inherit"
                                    style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: '4px' }}>
                                    <Badge color="primary" badgeContent={cartCount > 0 ? cartCount : offerCount}>
                                        {offerCount > 0 ? <StorefrontOutlinedIcon /> : <ShoppingCartOutlinedIcon />}
                                    </Badge>
                                </IconButton>
                            )}
                            {isLoggedIn &&
                                (isMedium ? (
                                    <MobileDrawer routes={accountRoutes} icon={actionBadge} />
                                ) : (
                                    <IconButton
                                        {...(ROUTE_ACCOUNT.componentProps as any)}
                                        color="inherit"
                                        style={{ marginTop: 'auto', marginBottom: 'auto' }}>
                                        {actionBadge}
                                    </IconButton>
                                ))}
                        </StyledToolbar>
                    </Container>
                </AppBar>
            </AppbarContainer>
        </header>
    );
};

export default CoffeehouseHeader;
